import { InputTextarea } from "primereact/inputtextarea";
import React, { useEffect } from "react";
import useMessages from "../../../Pages/Process/actions/useMessages";
import PropTypes from "prop-types";
import CustomeButton from "../../Button";
import Attachments from "./Attachments";
import "./conversations.scss";
import ConversationPopup from "./ConversationPopup";
import ErrorMessage from "./ErrorMessage";
import { useSelector } from "react-redux";
import FileUploadModal from "./FileUploadModal";
import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";
import { REQUEST_TYPES } from "~/Pages/Requests/useRequest";
import AttachmentConfirmationModal from "~/components/AttachmentConfirmationModal";
import { Carousel } from "primereact/carousel";
import { mergeCategories } from "~/Pages/Requests/components/new-request-modal/utils";
import { formatVersionedAttachmentDisplayName } from "~/utils/fileUtils";
import Categories from "./utils/categories.json";

const MessageInput = ({
  fetchMessages,
  entitydata,
  relatedType,
  serviceData = {},
  requestdata = {},
  requestType = "",
  processdata = {},
  allConversationAttachments = [],
  onRequestScrollToLatest,
  onSendButtonEnabled,
  onBeforeSend,
}) => {
  const {
    isPublic,
    isMessageLoading,
    fileSupported,
    config,
    getService,
    messageText,
    setMessageText,
    charCounter,
    setCharCounter,
    showConfirmDialog,
    setShowConfirmDialog,
    isDataValid,
    setIsDataValid,
    isMessageSent,
    setIsMessageSent,
    showUploadModal,
    setShowUploadModal,
    files,
    setFiles,
    sendMessage,
    inputTextAreaRef,
    toggleNotification,
    setToggleNotification,
    send,
    showAttachmentConfirmation,
    setShowAttachmentConfirmation,
  } = useMessages({
    fetchMessages,
    relatedType,
    requestType,
    serviceData,
    allConversationAttachments,
  });
  const { role } = useSelector((state) => state.login);
  const processdataFromRedux = useSelector(
    (state) => state.process?.processdata,
  );
  const effectiveProcessdata =
    processdata && Object.keys(processdata).length > 0
      ? processdata
      : processdataFromRedux || {};

  const { value: isDocumentStorageFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.DOCUMENT_STORAGE,
  );

  const getSubCategoryName = (categoryCode, subCategoryCode) => {
    const cat = Categories?.find((c) => c.code === categoryCode);
    const sub = cat?.subCategory?.find((s) => s.code === subCategoryCode);
    return sub?.name ?? subCategoryCode ?? "-";
  };

  const messageTextHandler = (event) => {
    const el = event.target;
    el.style.height = "44px";
    if (event.target.value) {
      el.style.height = Math.min(el.scrollHeight, 70) + "px";
    }
    setMessageText(event.target.value);
    setCharCounter(event.target.value.length);
  };

  const onFileRemove = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };
  const replaceFile = (fileInputOrEvent, fileToReplace) => {
    const selectedFile = fileInputOrEvent?.files?.[0];
    if (!selectedFile) return;
    let newFile;

    if (!fileSupported.includes(selectedFile.name.split(".").pop())) {
      newFile = { file: selectedFile, error: { state: true, visible: true } };
    } else {
      if (getFileSizeMB(selectedFile) > 20) {
        newFile = { file: selectedFile, error: { state: true, visible: true } };
      } else {
        newFile = {
          file: selectedFile,
          error: { state: false, visible: false },
        };
      }
    }

    setFiles((prevFiles) =>
      prevFiles.map((existingFileObj) =>
        existingFileObj.file === fileToReplace ? newFile : existingFileObj,
      ),
    );
  };

  const onUpdateVisibility = (fileToRemove) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file === fileToRemove
          ? { ...file, error: { state: true, visible: false } }
          : file,
      ),
    );
  };

  const uploadFileHandler = (file) => {
    setFiles((prevFiles) => [...prevFiles, ...file]);
  };

  const disableHandler = () => {
    if ((messageText || files.length > 0) && charCounter <= 4000) return false;
    else return true;
  };

  // Call onSendButtonEnabled when send button becomes enabled
  useEffect(() => {
    const isSendButtonEnabled = !disableHandler() && isDataValid;
    if (isSendButtonEnabled && onSendButtonEnabled) {
      onSendButtonEnabled();
    }
  }, [
    messageText,
    files.length,
    charCounter,
    isDataValid,
    onSendButtonEnabled,
  ]);

  const handleConfirm = async () => {
    // Remove commentId before sending
    onBeforeSend && onBeforeSend();
    // Request the list to scroll to latest when the new message appears
    onRequestScrollToLatest && onRequestScrollToLatest();
    await sendMessage();
    setShowConfirmDialog(false);
  };
  const getFileSizeMB = (file) => (file.size / (1024 * 1024)).toFixed(2);
  useEffect(() => {
    let hasError = false;
    for (const element of files) {
      if (element.error.state) {
        hasError = true;
        break;
      }
    }
    setIsDataValid(!hasError);
  }, [files]);
  const service = entitydata?.active_services?.map((data) => data?.name) || [];

  const normalizedFiles = files.map((x, i) => {
    const fileObj = x.file;
    const period =
      fileObj?.fileYearMonth?.year && fileObj?.fileYearMonth?.month
        ? `${fileObj.fileYearMonth.year}-${String(fileObj.fileYearMonth.month).padStart(2, "0")}`
        : null;

    return {
      ...fileObj,
      name: fileObj?.fileName || fileObj?.name,
      category: fileObj?.category?.code || fileObj?.category,
      subCategory:
        fileObj?.subcategory?.code ||
        fileObj?.subcategory?.name ||
        fileObj?.subcategoryName ||
        fileObj?.subcategory ||
        fileObj?.subCategory ||
        fileObj?.subCategoryName,
      period,
      blob_url: fileObj?.blob_url || `${fileObj?.name}-${i}`,
    };
  });

  // Build version key and 1-based version number for same subcategory + period
  const getVersionKey = (nf) => {
    const subCode =
      typeof nf?.subCategory === "object"
        ? nf.subCategory?.code
        : nf?.subCategory;
    return `${nf?.category ?? ""}_${subCode ?? ""}_${nf?.period ?? ""}`;
  };
  const versionNumberByIndex = normalizedFiles.map((nf, i) => {
    const key = getVersionKey(nf);
    const sameKeyIndices = normalizedFiles
      .map((f, idx) => (getVersionKey(f) === key ? idx : null))
      .filter((idx) => idx !== null);
    return sameKeyIndices.indexOf(i) + 1;
  });

  return (
    <div className="message-input-container">
      <InputTextarea
        ref={inputTextAreaRef}
        value={messageText}
        onChange={messageTextHandler}
        placeholder="Add your message here..."
        autoResize={false}
        className={`custom-textarea ${charCounter > 4000 && "error-input"}`}
      />
      {files.length > 0 && (
        <div className="attachment-layout">
          <h4>Attachments</h4>
          <div
            className={`attachment-carousel ${files.length <= 2 ? "no-padding" : ""}`}
          >
            <Carousel
              value={files}
              itemTemplate={(file) => {
                const f = file.file;
                const itemIndex = files.findIndex(
                  (entry) => entry === file || entry?.file === f,
                );
                const isPayrollService =
                  (relatedType === "PROCESS" || relatedType === "REQUEST") &&
                  (effectiveProcessdata?.serviceData?.name === "Payroll" ||
                    serviceData?.name === "Payroll" ||
                    requestdata?.serviceData?.name === "Payroll");

                let displayName;
                if (isPayrollService) {
                  displayName = f?.fileName || f?.name;
                } else {
                  const categoryCode = f?.category?.code || f?.category;
                  const subCategoryCode =
                    typeof f?.subcategory === "object"
                      ? f.subcategory?.code
                      : f?.subcategory;
                  const subCategoryName =
                    relatedType === "REQUEST" &&
                    typeof f?.subcategory === "object" &&
                    f.subcategory?.name
                      ? f.subcategory.name
                      : getSubCategoryName(categoryCode, subCategoryCode);
                  const periodMonthYear =
                    f?.fileYearMonth?.year && f?.fileYearMonth?.month
                      ? new Date(
                          f.fileYearMonth.year,
                          f.fileYearMonth.month - 1,
                          1,
                        ).toLocaleString("default", {
                          month: "long",
                          year: "numeric",
                        })
                      : null;
                  const versionNumber =
                    itemIndex >= 0 ? (versionNumberByIndex[itemIndex] ?? 1) : 1;
                  const extension = (f?.fileName || f?.name || "")
                    .split(".")
                    .pop()
                    .toLowerCase();
                  displayName = formatVersionedAttachmentDisplayName(
                    subCategoryName || "File",
                    periodMonthYear,
                    versionNumber,
                    extension,
                  );
                }

                return (
                  <Attachments
                    file={f}
                    key={itemIndex >= 0 ? itemIndex : f?.name}
                    fileHandler={() => onFileRemove(file)}
                    icon="pi-times"
                    progressBar={isMessageLoading}
                    error={file.error}
                    handleErrorVisibility={() => onUpdateVisibility(file)}
                    getFileSizeMB={getFileSizeMB}
                    handleReplace={(file, fileToReplace) =>
                      replaceFile(file, fileToReplace)
                    }
                    displayName={displayName}
                    useUploadActions={true}
                    showRemoveIconInsteadOfDownload={true}
                    openPreviewHandler={(file) => {
                      const url = file?.objectURL || URL.createObjectURL(file);
                      window.open(url, "_blank");
                    }}
                  />
                );
              }}
              numVisible={2}
              numScroll={2}
              showIndicators={false}
              showNavigators={files.length > 2}
              className="p-carousel-attachments"
            />
          </div>
        </div>
      )}

      {!isMessageSent && (
        <ErrorMessage
          summary={"File Upload Failed."}
          detail="Something went wrong. Please try uploading again."
          handleClose={() => {
            setIsMessageSent(true);
          }}
          otherAction={"Try Again"}
          handleOtherAction={send}
        />
      )}
      {charCounter > 4000 && (
        <div className="error-message-container">
          <i className=" pi pi-exclamation-circle" />
          <p className="error-message">
            Your text exceeds the maximum character limit
          </p>
        </div>
      )}
      <div className="actions-container">
        <p className="message-limit-text">
          {charCounter}/4000 characters allowed
        </p>
        <div className="action-btn-container">
          {isDocumentStorageFeatureEnabled &&
            requestType !== REQUEST_TYPES.ESCALATION && (
              <CustomeButton
                label="Add Attachment"
                variant="outlined"
                className="attachment-button  p-button-outlined"
                icon="pi pi-paperclip"
                iconPos="right"
                onClick={() => setShowUploadModal(true)}
              />
            )}
          <CustomeButton
            label="Send"
            variant="contained"
            className="send-button"
            icon="pi pi-send"
            iconPos="right"
            disabled={disableHandler() || !isDataValid || isMessageLoading}
            onClick={async () => {
              // Remove commentId before sending
              onBeforeSend && onBeforeSend();
              // Request scroll to latest once the message shows up
              onRequestScrollToLatest && onRequestScrollToLatest();
              if (files.length > 0) {
                setShowAttachmentConfirmation(true);
              } else {
                await send(entitydata, requestdata);
              }
            }}
            loading={
              isPublic ? (config ? isMessageLoading : false) : isMessageLoading
            }
          />
        </div>
      </div>
      <AttachmentConfirmationModal
        showModal={showAttachmentConfirmation}
        handleCloseModal={() => setShowAttachmentConfirmation(false)}
        handleConfirm={async () => {
          // Remove commentId before sending
          onBeforeSend && onBeforeSend();
          onRequestScrollToLatest && onRequestScrollToLatest();
          await send(entitydata, requestdata);
        }}
      />
      <ConversationPopup
        visible={showConfirmDialog}
        onConfirm={handleConfirm}
        isMessageLoading={isMessageLoading}
        toggleNotification={toggleNotification}
        setToggleNotification={setToggleNotification}
        setShowConfirmDialog={setShowConfirmDialog}
        role={role}
      />
      <FileUploadModal
        entitydata={entitydata}
        processdata={effectiveProcessdata}
        showModal={showUploadModal}
        fileSupported={fileSupported}
        hideUploadModal={() => setShowUploadModal(false)}
        setUploadedFile={uploadFileHandler}
        getService={getService}
        requestdata={requestdata}
        relatedType={relatedType}
        uploadedFile={files}
        categories={mergeCategories(
          relatedType === "REQUEST"
            ? [requestdata?.serviceData?.name]
            : [effectiveProcessdata?.serviceData?.name],
          role,
        )}
      />
    </div>
  );
};

MessageInput.propTypes = {
  fetchMessages: PropTypes.func,
  requestType: PropTypes.string,
  allConversationAttachments: PropTypes.arrayOf(PropTypes.object),
  onRequestScrollToLatest: PropTypes.func,
  onSendButtonEnabled: PropTypes.func,
  onBeforeSend: PropTypes.func,
};

export default MessageInput;
